import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import {
  TrendingUp,
  DollarSign,
  Target,
  Clock,
  Award,
  CheckCircle,
  FileText,
  Users,
  Building2,
  Loader2,
  Info,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  X
} from 'lucide-react';
import {
  BarChart, Bar, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { formatCurrency } from '../utils/format';
import FiscalYearSelector from '../components/FiscalYearSelector';

const COLORS = ['#6366F1', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6', '#14B8A6', '#F43F5E', '#3B82F6'];

type Tab = 'overview' | 'commercial' | 'temporal' | 'financial' | 'forecast';

interface PerformanceData {
  // KPIs principaux
  totalCA: number;
  totalObjective: number;
  realizationRate: number;
  totalTransactions: number;
  averageTicket: number;
  netMargin: number;
  
  // Taux de conversion
  conversionRates: {
    potentialToCompromis: number;
    compromisToActe: number;
    acteToEncaisse: number;
    globalConversion: number;
  };
  
  // Analyse des mandats
  mandateAnalysis: {
    type: string;
    count: number;
    ca: number;
    averageCA: number;
    conversionRate: number;
  }[];
  
  // Performance par origine
  originPerformance: {
    origin: string;
    count: number;
    ca: number;
    conversionRate: number;
  }[];
  
  // Cycles de vente
  salesCycles: {
    averageCompromisToActe: number;
    averageActeToEncaissement: number;
    averageTotalCycle: number;
  };
  
  // Évolution mensuelle
  monthlyEvolution: {
    month: string;
    ca: number;
    transactions: number;
    objective: number;
    previousYearCA: number;
  }[];
  
  // Performance par société
  companyPerformance: {
    company: string;
    ca: number;
    transactions: number;
    objective: number;
    realizationRate: number;
  }[];
  
  // Rétrocessions
  retrocessionAnalysis: {
    type: string;
    total: number;
    count: number;
    percentage: number;
  }[];
  
  // Pipeline prévisionnel
  pipeline: {
    potentialCA: number;
    compromisCA: number;
    expectedCA: number;
    probability: number;
  };
  
  // Alertes
  alerts: {
    type: 'danger' | 'warning' | 'info';
    message: string;
    value?: number;
  }[];
  
  // Performance par type de bien
  propertyTypePerformance: {
    type: string;
    count: number;
    ca: number;
    averagePrice: number;
    averageDuration: number;
  }[];
  
  // Top performers
  topNegotiators: {
    name: string;
    ca: number;
    transactions: number;
    realizationRate: number;
  }[];
  
  topAgencies: {
    name: string;
    ca: number;
    transactions: number;
    realizationRate: number;
  }[];
}

// Composant InfoTooltip
interface InfoTooltipProps {
  title: string;
  description: string;
  formula?: string;
}

function InfoTooltip({ title, description, formula }: InfoTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <Info size={16} />
      </button>
      
      {isVisible && (
        <div className="absolute top-8 right-0 w-80 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50">
          <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
          <p className="text-sm text-gray-700 mb-2">{description}</p>
          {formula && (
            <div className="bg-gray-50 p-2 rounded text-xs font-mono text-gray-800">
              {formula}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Composant KPI Card
interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  tooltip?: {
    title: string;
    description: string;
    formula?: string;
  };
}

function KPICard({ title, value, subtitle, icon, color, trend, trendValue, tooltip }: KPICardProps) {
  const getTrendColor = () => {
    if (trend === 'up') return 'text-emerald-600 bg-emerald-50';
    if (trend === 'down') return 'text-red-500 bg-red-50';
    return 'text-slate-500 bg-slate-100';
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          {icon}
        </div>
        {tooltip && <InfoTooltip {...tooltip} />}
      </div>
      <p className="text-xs font-medium text-slate-500 mb-1">{title}</p>
      <p className="text-2xl font-bold text-slate-800">{value}</p>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
      {trend && trendValue && (
        <div className={`inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-semibold ${getTrendColor()}`}>
          {trend === 'up' ? <ArrowUpRight size={12} /> : trend === 'down' ? <ArrowDownRight size={12} /> : <Minus size={12} />}
          <span>{trendValue}</span>
        </div>
      )}
    </div>
  );
}

function calculatePerformanceData(
  transactions: any[],
  previousYearTransactions: any[],
  startDate: string,
  endDate: string,
  _fiscalYear: number,
  totalObjective: number = 0,
  filterAgencies: Set<string> = new Set()
): PerformanceData {
  // Séparer les transactions par statut
  const potentialTransactions = transactions.filter(t => t.status === 'potentiel');
  const realizedTransactions = transactions.filter(t => !['potentiel', 'annule'].includes(t.status));
  const compromisTransactions = transactions.filter(t => t.status === 'compromis');
  const acteTransactions = transactions.filter(t => ['acte', 'encaisse', 'cloture'].includes(t.status));
  const encaisseTransactions = transactions.filter(t => ['encaisse', 'cloture'].includes(t.status));

  // Calculer le Honoraires nets et les honoraires nets
  const totalCA = realizedTransactions.reduce((sum, t) => {
    const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
    return sum + (t.fees_without_tax - retrocessions);
  }, 0);

  const totalFees = realizedTransactions.reduce((sum, t) => sum + t.fees_without_tax, 0);
  const totalRetrocessions = realizedTransactions.reduce((sum, t) => {
    return sum + (t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0);
  }, 0);

  // totalObjective est passé en paramètre depuis agency_annual_objectives

  // Taux de conversion
  const conversionRates = {
    potentialToCompromis: potentialTransactions.length > 0
      ? (compromisTransactions.length / (potentialTransactions.length + compromisTransactions.length)) * 100
      : 0,
    compromisToActe: compromisTransactions.length > 0
      ? (acteTransactions.length / compromisTransactions.length) * 100
      : 0,
    acteToEncaisse: acteTransactions.length > 0
      ? (encaisseTransactions.length / acteTransactions.length) * 100
      : 0,
    globalConversion: potentialTransactions.length > 0
      ? (encaisseTransactions.length / (potentialTransactions.length + realizedTransactions.length)) * 100
      : 0
  };

  // Analyse des mandats
  const mandateTypes = ['exclu', 'simple', 'privilège'];
  const mandateAnalysis = mandateTypes.map(type => {
    const mandateTransactions = realizedTransactions.filter(t => t.mandate_type === type);
    const mandateCA = mandateTransactions.reduce((sum, t) => {
      const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
      return sum + (t.fees_without_tax - retrocessions);
    }, 0);
    return {
      type: type === 'exclu' ? 'Exclusif' : type === 'simple' ? 'Simple' : 'Privilège',
      count: mandateTransactions.length,
      ca: mandateCA,
      averageCA: mandateTransactions.length > 0 ? mandateCA / mandateTransactions.length : 0,
      conversionRate: 0
    };
  });

  // Performance par origine du lead
  const leadOrigins = [...new Set(realizedTransactions.map(t => t.lead_origin).filter(Boolean))];
  const originPerformance = leadOrigins.map(origin => {
    const originTransactions = realizedTransactions.filter(t => t.lead_origin === origin);
    const originCA = originTransactions.reduce((sum, t) => {
      const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
      return sum + (t.fees_without_tax - retrocessions);
    }, 0);
    return {
      origin: origin || 'Non renseigné',
      count: originTransactions.length,
      ca: originCA,
      conversionRate: 0
    };
  }).sort((a, b) => b.ca - a.ca).slice(0, 10);

  // Cycles de vente
  const cyclesData = realizedTransactions.filter(t => t.compromise_date && t.acte_date);
  const averageCompromisToActe = cyclesData.length > 0
    ? cyclesData.reduce((sum, t) => {
        const compromisDate = new Date(t.compromise_date);
        const acteDate = new Date(t.acte_date);
        return sum + (acteDate.getTime() - compromisDate.getTime()) / (1000 * 60 * 60 * 24);
      }, 0) / cyclesData.length
    : 0;

  const encaissementData = encaisseTransactions.filter(t => t.acte_date && t.encaissement_date);
  const averageActeToEncaissement = encaissementData.length > 0
    ? encaissementData.reduce((sum, t) => {
        const acteDate = new Date(t.acte_date);
        const encaissementDate = new Date(t.encaissement_date);
        return sum + (encaissementDate.getTime() - acteDate.getTime()) / (1000 * 60 * 60 * 24);
      }, 0) / encaissementData.length
    : 0;

  const salesCycles = {
    averageCompromisToActe,
    averageActeToEncaissement,
    averageTotalCycle: averageCompromisToActe + averageActeToEncaissement
  };

  // Évolution mensuelle
  const monthlyData = new Map<string, { ca: number; transactions: number; previousYearCA: number }>();
  let currentDate = new Date(startDate);
  const endDateObj = new Date(endDate);
  while (currentDate <= endDateObj) {
    const monthKey = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`;
    monthlyData.set(monthKey, { ca: 0, transactions: 0, previousYearCA: 0 });
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  realizedTransactions.forEach(t => {
    if (t.compromise_date) {
      const date = new Date(t.compromise_date);
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      const d = monthlyData.get(monthKey);
      if (d) {
        const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
        d.ca += t.fees_without_tax - retrocessions;
        d.transactions++;
      }
    }
  });
  previousYearTransactions.forEach(t => {
    if (t.compromise_date) {
      const date = new Date(t.compromise_date);
      const monthKey = `${date.getFullYear() + 1}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      const d = monthlyData.get(monthKey);
      if (d) {
        const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
        d.previousYearCA += t.fees_without_tax - retrocessions;
      }
    }
  });
  const monthlyEvolution = Array.from(monthlyData.entries())
    .map(([month, d]) => ({
      month: new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }),
      ca: d.ca,
      transactions: d.transactions,
      objective: 0,
      previousYearCA: d.previousYearCA
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

  // Performance par société
  const companies = ['KI', 'KA', 'DRYADE'];
  const companyPerformance = companies.map(company => {
    const companyTransactions = realizedTransactions.filter(t =>
      t.transaction_agencies?.some((ta: any) => ta.agencies?.company === company)
    );
    const companyCA = companyTransactions.reduce((sum, t) => {
      const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
      return sum + (t.fees_without_tax - retrocessions);
    }, 0);
    return { company, ca: companyCA, transactions: companyTransactions.length, objective: 0, realizationRate: 0 };
  });

  // Analyse des rétrocessions
  const retrocessionTypes = new Map<string, { total: number; count: number }>();
  realizedTransactions.forEach(t => {
    t.transaction_retrocessions?.forEach((r: any) => {
      const current = retrocessionTypes.get(r.type) || { total: 0, count: 0 };
      current.total += r.amount;
      current.count++;
      retrocessionTypes.set(r.type, current);
    });
  });
  const retrocessionAnalysis = Array.from(retrocessionTypes.entries())
    .map(([type, d]) => ({
      type,
      total: d.total,
      count: d.count,
      percentage: totalFees > 0 ? (d.total / totalFees) * 100 : 0
    }))
    .sort((a, b) => b.total - a.total);

  // Pipeline prévisionnel
  const potentialCA = potentialTransactions.reduce((sum, t) => {
    const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
    return sum + (t.fees_without_tax - retrocessions);
  }, 0);
  const compromisCA = compromisTransactions.reduce((sum, t) => {
    const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
    return sum + (t.fees_without_tax - retrocessions);
  }, 0);
  const pipeline = {
    potentialCA,
    compromisCA,
    expectedCA: totalCA + (compromisCA * 0.8) + (potentialCA * 0.3),
    probability: conversionRates.globalConversion
  };

  // Alertes
  const alerts: PerformanceData['alerts'] = [];
  if (totalObjective > 0 && (totalCA / totalObjective) < 0.5) {
    alerts.push({ type: 'danger', message: 'Objectif en danger : moins de 50% réalisé', value: (totalCA / totalObjective) * 100 });
  }
  if (conversionRates.globalConversion < 20) {
    alerts.push({ type: 'warning', message: 'Taux de conversion global faible', value: conversionRates.globalConversion });
  }
  if (salesCycles.averageTotalCycle > 90) {
    alerts.push({ type: 'warning', message: 'Cycle de vente long (> 90 jours)', value: salesCycles.averageTotalCycle });
  }

  // Performance par type de bien
  const propertyTypes = [...new Set(realizedTransactions.map(t => t.property_type).filter(Boolean))];
  const propertyTypePerformance = propertyTypes.map(type => {
    const typeTransactions = realizedTransactions.filter(t => t.property_type === type);
    const typeCA = typeTransactions.reduce((sum, t) => {
      const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
      return sum + (t.fees_without_tax - retrocessions);
    }, 0);
    const avgPrice = typeTransactions.reduce((sum, t) => sum + t.sale_price, 0) / typeTransactions.length;
    const durationData = typeTransactions.filter(t => t.compromise_date && t.acte_date);
    const avgDuration = durationData.length > 0
      ? durationData.reduce((sum, t) => {
          const compromisDate = new Date(t.compromise_date);
          const acteDate = new Date(t.acte_date);
          return sum + (acteDate.getTime() - compromisDate.getTime()) / (1000 * 60 * 60 * 24);
        }, 0) / durationData.length
      : 0;
    return { type: type || 'Non renseigné', count: typeTransactions.length, ca: typeCA, averagePrice: avgPrice, averageDuration: avgDuration };
  }).sort((a, b) => b.ca - a.ca);

  // Top négociateurs
  const negotiatorStats = new Map<string, { ca: number; transactions: number }>();
  realizedTransactions.forEach(t => {
    t.transaction_negotiators?.forEach((tn: any) => {
      // Si un filtre agence est actif, n'inclure que les négociateurs de ces agences
      if (filterAgencies.size > 0 && tn.agency && !filterAgencies.has(tn.agency)) return;
      const negotiatorName = `${tn.negotiator.firstname} ${tn.negotiator.lastname}`;
      const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
      const netFees = t.fees_without_tax - retrocessions;
      const negotiatorCA = (netFees * tn.percentage) / 100;
      const current = negotiatorStats.get(negotiatorName) || { ca: 0, transactions: 0 };
      current.ca += negotiatorCA;
      current.transactions++;
      negotiatorStats.set(negotiatorName, current);
    });
  });
  const topNegotiators = Array.from(negotiatorStats.entries())
    .map(([name, d]) => ({ name, ca: d.ca, transactions: d.transactions, realizationRate: 0 }))
    .sort((a, b) => b.ca - a.ca)
    .slice(0, 10);

  // Top agences
  const agencyStats = new Map<string, { ca: number; transactions: number }>();
  realizedTransactions.forEach(t => {
    t.transaction_agencies?.forEach((ta: any) => {
      const agencyName = ta.agency;
      const retrocessions = t.transaction_retrocessions?.reduce((s: number, r: any) => s + r.amount, 0) || 0;
      const netFees = t.fees_without_tax - retrocessions;
      const current = agencyStats.get(agencyName) || { ca: 0, transactions: 0 };
      current.ca += netFees;
      current.transactions++;
      agencyStats.set(agencyName, current);
    });
  });
  const topAgencies = Array.from(agencyStats.entries())
    .map(([name, d]) => ({ name, ca: d.ca, transactions: d.transactions, realizationRate: 0 }))
    .sort((a, b) => b.ca - a.ca)
    .slice(0, 10);

  return {
    totalCA,
    totalObjective,
    realizationRate: totalObjective > 0 ? (totalCA / totalObjective) * 100 : 0,
    totalTransactions: realizedTransactions.length,
    averageTicket: realizedTransactions.length > 0 ? totalCA / realizedTransactions.length : 0,
    netMargin: totalFees > 0 ? ((totalFees - totalRetrocessions) / totalFees) * 100 : 0,
    conversionRates,
    mandateAnalysis,
    originPerformance,
    salesCycles,
    monthlyEvolution,
    companyPerformance,
    retrocessionAnalysis,
    pipeline,
    alerts,
    propertyTypePerformance,
    topNegotiators,
    topAgencies
  };
}

export default function Performance() {
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rawTransactions, setRawTransactions] = useState<any[]>([]);
  const [rawPreviousTransactions, setRawPreviousTransactions] = useState<any[]>([]);
  const [rawObjectives, setRawObjectives] = useState<{ agency_name: string; annual_objective: number }[]>([]);
  const [currentDates, setCurrentDates] = useState({ startDate: '', endDate: '' });

  // Modal délai mandat → compromis
  const [mandatDelayModal, setMandatDelayModal] = useState<{ type: string; label: string } | null>(null);

  // Filtres globaux
  const [filterCompany, setFilterCompany] = useState<string>('ALL');
  const [filterAgencies, setFilterAgencies] = useState<Set<string>>(new Set());
  const [agencyDropdownOpen, setAgencyDropdownOpen] = useState(false);
  
  const [fiscalYear, setFiscalYear] = useState<number>(() => {
    const today = new Date();
    const month = today.getMonth() + 1;
    return month <= 4 ? today.getFullYear() - 1 : today.getFullYear();
  });
  const [period, setPeriod] = useState<'year' | 'quarter' | 'month'>('year');
  const [selectedPeriod, setSelectedPeriod] = useState('1');

  useEffect(() => {
    loadPerformanceData();
  }, [fiscalYear, period, selectedPeriod]);

  const loadPerformanceData = async () => {
    try {
      setLoading(true);
      setError('');

      // Calculer les dates
      let startDate: string;
      let endDate: string;
      let previousYearStartDate: string;
      let previousYearEndDate: string;

      if (period === 'year') {
        startDate = `${fiscalYear}-04-01`;
        endDate = `${fiscalYear + 1}-03-31`;
        previousYearStartDate = `${fiscalYear - 1}-04-01`;
        previousYearEndDate = `${fiscalYear}-03-31`;
      } else if (period === 'quarter') {
        const quarterMap = {
          '1': { start: '04', end: '06' },
          '2': { start: '07', end: '09' },
          '3': { start: '10', end: '12' },
          '4': { start: '01', end: '03' }
        };
        const quarter = quarterMap[selectedPeriod as keyof typeof quarterMap];
        const year = selectedPeriod === '4' ? fiscalYear + 1 : fiscalYear;
        const prevYear = selectedPeriod === '4' ? fiscalYear : fiscalYear - 1;
        startDate = `${year}-${quarter.start}-01`;
        endDate = `${year}-${quarter.end}-${new Date(year, parseInt(quarter.end), 0).getDate()}`;
        previousYearStartDate = `${prevYear}-${quarter.start}-01`;
        previousYearEndDate = `${prevYear}-${quarter.end}-${new Date(prevYear, parseInt(quarter.end), 0).getDate()}`;
      } else {
        const month = selectedPeriod.padStart(2, '0');
        const year = parseInt(selectedPeriod) <= 3 ? fiscalYear + 1 : fiscalYear;
        const prevYear = parseInt(selectedPeriod) <= 3 ? fiscalYear : fiscalYear - 1;
        startDate = `${year}-${month}-01`;
        endDate = `${year}-${month}-${new Date(year, parseInt(month), 0).getDate()}`;
        previousYearStartDate = `${prevYear}-${month}-01`;
        previousYearEndDate = `${prevYear}-${month}-${new Date(prevYear, parseInt(month), 0).getDate()}`;
      }

      // Charger toutes les transactions (actuelles et année précédente) + objectifs
      const [currentResponse, previousResponse, objectivesResponse] = await Promise.all([
        supabase
          .from('transactions')
          .select(`
            id,
            property_number,
            sale_price,
            fees_without_tax,
            status,
            mandate_type,
            property_type,
            lead_origin,
            mandate_date,
            compromise_date,
            acte_date,
            encaissement_date,
            transaction_agencies (
              agency,
              agencies (
                company
              )
            ),
            transaction_negotiators (
              agency,
              negotiator:negotiators (
                id,
                firstname,
                lastname
              ),
              percentage
            ),
            transaction_retrocessions (
              type,
              amount
            )
          `)
          .or(`and(status.eq.potentiel),and(status.in.(compromis,acte,encaisse,cloture),compromise_date.gte.${startDate},compromise_date.lte.${endDate})`),
        
        supabase
          .from('transactions')
          .select(`
            id,
            sale_price,
            fees_without_tax,
            status,
            compromise_date
          `)
          .in('status', ['compromis', 'acte', 'encaisse', 'cloture'])
          .gte('compromise_date', previousYearStartDate)
          .lte('compromise_date', previousYearEndDate),

        supabase
          .from('agency_annual_objectives')
          .select('agency_name, annual_objective')
          .eq('fiscal_year', fiscalYear)
      ]);

      if (currentResponse.error) throw currentResponse.error;
      if (previousResponse.error) throw previousResponse.error;

      const transactions = currentResponse.data || [];
      const previousYearTransactions = previousResponse.data || [];

      setRawTransactions(transactions);
      setRawPreviousTransactions(previousYearTransactions);
      setRawObjectives(objectivesResponse.data || []);
      setCurrentDates({ startDate, endDate });
    } catch (err: any) {
      console.error('Error loading performance data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // calculatePerformanceData is defined outside this component (module-level pure function)

  const handlePeriodChange = (year: number, newPeriod: 'year' | 'quarter' | 'month', newSelectedPeriod: string) => {
    setFiscalYear(year);
    setPeriod(newPeriod);
    setSelectedPeriod(newSelectedPeriod);
  };

  // Map agency → company (from raw transactions)
  const agencyToCompanyMap = useMemo(() => {
    const map = new Map<string, string>();
    rawTransactions.forEach(t =>
      t.transaction_agencies?.forEach((ta: any) => {
        if (ta.agency && ta.agencies?.company) map.set(ta.agency, ta.agencies.company);
      })
    );
    return map;
  }, [rawTransactions]);

  // Companies list
  const companiesForFilter = useMemo(() => {
    const set = new Set<string>();
    rawTransactions.forEach(t =>
      t.transaction_agencies?.forEach((ta: any) => {
        if (ta.agencies?.company) set.add(ta.agencies.company);
      })
    );
    return Array.from(set).sort();
  }, [rawTransactions]);

  // Agencies list — filtered by selected company
  const agenciesForFilter = useMemo(() => {
    const set = new Set<string>();
    rawTransactions.forEach(t =>
      t.transaction_agencies?.forEach((ta: any) => {
        if (ta.agency) {
          if (filterCompany === 'ALL' || ta.agencies?.company === filterCompany) set.add(ta.agency);
        }
      })
    );
    return Array.from(set).sort();
  }, [rawTransactions, filterCompany]);

  // Filtered transactions (global)
  const filteredTransactions = useMemo(() => {
    const noCompany = filterCompany === 'ALL';
    const noAgency = filterAgencies.size === 0;
    if (noCompany && noAgency) return rawTransactions;
    return rawTransactions.filter(t =>
      t.transaction_agencies?.some((ta: any) => {
        const companyOk = noCompany || ta.agencies?.company === filterCompany;
        const agencyOk = noAgency || filterAgencies.has(ta.agency);
        return companyOk && agencyOk;
      })
    );
  }, [rawTransactions, filterCompany, filterAgencies]);

  // Filtered objective
  const filteredObjective = useMemo(() => {
    const noCompany = filterCompany === 'ALL';
    const noAgency = filterAgencies.size === 0;
    if (noCompany && noAgency) {
      return rawObjectives.reduce((s, o) => s + (o.annual_objective || 0), 0);
    }
    return rawObjectives.filter(o => {
      if (!noAgency) return filterAgencies.has(o.agency_name);
      if (!noCompany) return agencyToCompanyMap.get(o.agency_name) === filterCompany;
      return true;
    }).reduce((s, o) => s + (o.annual_objective || 0), 0);
  }, [rawObjectives, filterCompany, filterAgencies, agencyToCompanyMap]);

  // data — reactive to filters
  const data = useMemo((): PerformanceData | null => {
    if (!currentDates.startDate) return null;
    return calculatePerformanceData(
      filteredTransactions,
      rawPreviousTransactions,
      currentDates.startDate,
      currentDates.endDate,
      fiscalYear,
      filteredObjective,
      filterAgencies
    );
  }, [filteredTransactions, rawPreviousTransactions, currentDates, fiscalYear, filteredObjective, filterAgencies]);

  // Délai Mandat → Compromis (uses filteredTransactions)
  const mandatToCompromisDelays = useMemo(() => {
    const withDates = filteredTransactions.filter(t => t.mandate_date && t.compromise_date);
    const mandateTypes = ['exclu', 'simple', 'privilège', 'recherche', 'délégation'];
    return mandateTypes.map(type => {
      const group = withDates.filter(t => t.mandate_type === type);
      if (group.length === 0) return null;
      const avg = group.reduce((sum, t) => {
        const days = (new Date(t.compromise_date).getTime() - new Date(t.mandate_date).getTime()) / (1000 * 60 * 60 * 24);
        return sum + days;
      }, 0) / group.length;
      const label: Record<string, string> = {
        exclu: 'Exclusif', simple: 'Simple', 'privilège': 'Privilège',
        recherche: 'Recherche', 'délégation': 'Délégation'
      };
      return { type: label[type] ?? type, rawType: type, avgDays: Math.round(avg), count: group.length };
    }).filter((d): d is NonNullable<typeof d> => d !== null);
  }, [filteredTransactions]);

  // Données détaillées pour la modal (triées décroissant par délai)
  const mandatDelayModalRows = useMemo(() => {
    if (!mandatDelayModal) return [];
    const withDates = filteredTransactions.filter(t => t.mandate_date && t.compromise_date && t.mandate_type === mandatDelayModal.type);
    return withDates
      .map(t => {
        const days = Math.round((new Date(t.compromise_date).getTime() - new Date(t.mandate_date).getTime()) / (1000 * 60 * 60 * 24));
        return { id: t.id, days, propertyNumber: t.property_number ?? '—', mandateDate: t.mandate_date, compromiseDate: t.compromise_date };
      })
      .sort((a, b) => b.days - a.days);
  }, [mandatDelayModal, filteredTransactions]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        <p className="text-sm text-slate-500">Chargement des performances…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">{error}</div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-600 text-sm">Aucune donnée disponible</div>
    );
  }

  const tabs = [
    { id: 'overview'   as Tab, label: "Vue d'ensemble",          Icon: TrendingUp },
    { id: 'commercial' as Tab, label: 'Performance Commerciale', Icon: Users      },
    { id: 'temporal'   as Tab, label: 'Analyse Temporelle',      Icon: Clock      },
    { id: 'financial'  as Tab, label: 'Analyse Financière',      Icon: DollarSign },
    { id: 'forecast'   as Tab, label: 'Prévisions',              Icon: Target     },
  ];

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 right-32 w-40 h-40 bg-indigo-500/10 rounded-full translate-y-1/2 pointer-events-none" />
        <div className="relative px-7 pt-6 pb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
              <TrendingUp size={22} className="text-violet-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Performance</h1>
              <p className="text-sm text-slate-400 mt-0.5 flex items-center gap-1.5">
                <span className="text-violet-300 font-semibold">{filteredTransactions.length}</span>
                {' '}transaction{filteredTransactions.length !== 1 ? 's' : ''} analysée{filteredTransactions.length !== 1 ? 's' : ''}
                <span className="relative group inline-flex">
                  <Info size={13} className="text-slate-500 cursor-pointer hover:text-violet-300 transition-colors" />
                  <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-6 hidden group-hover:block z-[9999] w-52 drop-shadow-2xl">
                    <div className="w-2 h-2 bg-slate-800 border-l border-t border-white/10 rotate-45 mx-auto mb-[-5px] relative z-10" />
                    <div className="bg-slate-800 rounded-xl p-3 text-xs text-left border border-white/10">
                      {[
                        { label: 'Potentiel', value: filteredTransactions.filter(t => t.status === 'potentiel').length, color: 'bg-blue-400' },
                        { label: 'Compromis', value: filteredTransactions.filter(t => t.status === 'compromis').length, color: 'bg-amber-400' },
                        { label: 'Acté',      value: filteredTransactions.filter(t => t.status === 'acte').length,      color: 'bg-emerald-400' },
                        { label: 'Encaissé',  value: filteredTransactions.filter(t => t.status === 'encaisse').length,  color: 'bg-violet-400' },
                        { label: 'Clôturé',   value: filteredTransactions.filter(t => t.status === 'cloture').length,   color: 'bg-slate-400' },
                        { label: 'Annulé',    value: filteredTransactions.filter(t => t.status === 'annule').length,    color: 'bg-red-400' },
                      ].filter(s => s.value > 0).map(s => (
                        <div key={s.label} className="flex items-center justify-between gap-3 py-1">
                          <span className="flex items-center gap-2 text-slate-300">
                            <span className={`w-2 h-2 rounded-full ${s.color} shrink-0`} />
                            {s.label}
                          </span>
                          <span className="font-bold text-white">{s.value}</span>
                        </div>
                      ))}
                      <div className="mt-2 pt-2 border-t border-white/10 flex justify-between text-slate-400">
                        <span>Total</span>
                        <span className="font-bold text-white">{filteredTransactions.length}</span>
                      </div>
                    </div>
                  </div>
                </span>
              </p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <FiscalYearSelector value={fiscalYear} period={period} selectedPeriod={selectedPeriod} onChange={handlePeriodChange} variant="dark" />
          </div>
        </div>

        {/* Tab bar */}
        <div className="relative px-7 pb-4 flex items-center gap-1 border-t border-white/10 pt-3 overflow-x-auto">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
                activeTab === id
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-white/55 hover:text-white hover:bg-white/10'
              }`}
            >
              <Icon size={15} />
              {label}
            </button>
          ))}
        </div>
      </div>


      {/* ── Filtres globaux ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-3.5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 shrink-0">
          <Building2 size={15} className="text-slate-400" />
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Filtrer par</span>
        </div>

        {/* Entité */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-500 shrink-0">Entité</label>
          <select
            value={filterCompany}
            onChange={e => { setFilterCompany(e.target.value); setFilterAgencies(new Set()); }}
            className="pl-3 pr-7 py-1.5 text-sm border border-slate-200 rounded-xl text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
          >
            <option value="ALL">Toutes les entités</option>
            {companiesForFilter.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Agences multi-select dropdown */}
        <div className="flex items-center gap-2 relative">
          <label className="text-xs font-medium text-slate-500 shrink-0">Agences</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setAgencyDropdownOpen(v => !v)}
              className="flex items-center gap-2 pl-3 pr-3 py-1.5 text-sm border border-slate-200 rounded-xl text-slate-700 bg-white hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/40 transition-colors min-w-[180px] justify-between"
            >
              <span className="truncate">
                {filterAgencies.size === 0
                  ? 'Toutes les agences'
                  : filterAgencies.size === 1
                    ? Array.from(filterAgencies)[0]
                    : `${filterAgencies.size} agences sélectionnées`}
              </span>
              <svg className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform ${agencyDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>

            {agencyDropdownOpen && (
              <>
                {/* Overlay to close */}
                <div className="fixed inset-0 z-[190]" onClick={() => setAgencyDropdownOpen(false)} />
                <div className="absolute left-0 top-full mt-1.5 z-[200] bg-white rounded-2xl border border-slate-200 shadow-xl py-2 min-w-[220px] max-h-64 overflow-y-auto">
                  {/* Tout sélectionner / désélectionner */}
                  <div className="px-3 pb-2 mb-1 border-b border-slate-100">
                    <button
                      type="button"
                      onClick={() => setFilterAgencies(
                        filterAgencies.size === agenciesForFilter.length
                          ? new Set()
                          : new Set(agenciesForFilter)
                      )}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      {filterAgencies.size === agenciesForFilter.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                    </button>
                  </div>

                  {agenciesForFilter.map(agency => (
                    <label
                      key={agency}
                      className="flex items-center gap-2.5 px-3 py-2 hover:bg-indigo-50/60 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={filterAgencies.has(agency)}
                        onChange={() => {
                          const next = new Set(filterAgencies);
                          next.has(agency) ? next.delete(agency) : next.add(agency);
                          setFilterAgencies(next);
                        }}
                        className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600"
                      />
                      <span className="text-sm text-slate-700 truncate">{agency}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Pills agences sélectionnées */}
        {filterAgencies.size > 0 && filterAgencies.size <= 3 && (
          <div className="flex flex-wrap gap-1">
            {Array.from(filterAgencies).map(a => (
              <span key={a} className="flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full text-xs font-medium">
                {a}
                <button onClick={() => { const n = new Set(filterAgencies); n.delete(a); setFilterAgencies(n); }} className="hover:text-red-500 transition-colors leading-none">×</button>
              </span>
            ))}
          </div>
        )}

        {(filterCompany !== 'ALL' || filterAgencies.size > 0) && (
          <div className="ml-auto flex items-center gap-3">
            <span className="text-xs text-indigo-600 font-semibold bg-indigo-50 px-2.5 py-1 rounded-full">
              {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={() => { setFilterCompany('ALL'); setFilterAgencies(new Set()); }}
              className="text-xs text-slate-500 hover:text-red-500 font-medium transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* KPIs principaux */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <KPICard
              title="Honoraires Nets"
              value={formatCurrency(data.totalCA)}
              subtitle={`${data.totalTransactions} transactions`}
              icon={<DollarSign className="w-6 h-6" />}
              color="bg-indigo-50 text-indigo-600"
              tooltip={{
                title: "Chiffre d'Affaires Total",
                description: "Somme des honoraires nets HT (après rétrocessions) de toutes les transactions réalisées.",
                formula: "Σ(Honoraires HT - Rétrocessions)"
              }}
            />

            <KPICard
              title="Taux de Réalisation"
              value={`${data.realizationRate.toFixed(1)}%`}
              subtitle={`Objectif: ${formatCurrency(data.totalObjective)}`}
              icon={<Target className="w-6 h-6" />}
              color="bg-emerald-50 text-emerald-600"
              trend={data.realizationRate >= 100 ? 'up' : data.realizationRate >= 75 ? 'neutral' : 'down'}
              trendValue={`${data.realizationRate >= 100 ? '+' : ''}${(data.realizationRate - 100).toFixed(1)}%`}
              tooltip={{
                title: "Taux de Réalisation",
                description: "Pourcentage du CA réalisé par rapport à l'objectif fixé.",
                formula: "(Honoraires Réalisés / Objectif) × 100"
              }}
            />

            <KPICard
              title="Ticket Moyen"
              value={formatCurrency(data.averageTicket)}
              subtitle="Par transaction"
              icon={<Award className="w-6 h-6" />}
              color="bg-violet-50 text-violet-600"
              tooltip={{
                title: "Ticket Moyen",
                description: "Chiffre d'affaires moyen par transaction réalisée.",
                formula: "Honoraires Nets / Nombre de transactions"
              }}
            />

            <KPICard
              title="Marge Nette"
              value={`${data.netMargin.toFixed(1)}%`}
              subtitle="Après rétrocessions"
              icon={<TrendingUp className="w-6 h-6" />}
              color="bg-amber-50 text-amber-600"
              tooltip={{
                title: "Marge Nette",
                description: "Pourcentage des honoraires conservés après déduction des rétrocessions.",
                formula: "((Honoraires HT - Rétrocessions) / Honoraires HT) × 100"
              }}
            />

            <KPICard
              title="Taux de Conversion"
              value={`${data.conversionRates.globalConversion.toFixed(1)}%`}
              subtitle="Potentiel → Encaissé"
              icon={<CheckCircle className="w-6 h-6" />}
              color="bg-teal-50 text-teal-600"
              tooltip={{
                title: "Taux de Conversion Global",
                description: "Pourcentage de transactions potentielles qui aboutissent à un encaissement.",
                formula: "(Encaissés / (Potentiels + Réalisés)) × 100"
              }}
            />

            <KPICard
              title="Cycle de Vente"
              value={`${Math.round(data.salesCycles.averageTotalCycle)} jours`}
              subtitle="Compromis → Encaissement"
              icon={<Clock className="w-6 h-6" />}
              color="bg-pink-50 text-pink-600"
              tooltip={{
                title: "Cycle de Vente Moyen",
                description: "Durée moyenne entre la signature du compromis et l'encaissement.",
                formula: "Moyenne(Date Encaissement - Date Compromis)"
              }}
            />
          </div>

          {/* Graphiques principaux */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Évolution mensuelle */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Évolution Mensuelle des Honoraires</h3>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={data.monthlyEvolution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`} />
                  <Tooltip formatter={(value: any) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="ca" name="Honoraires Actuels" fill="#3B82F6" />
                  <Line 
                    type="monotone" 
                    dataKey="previousYearCA" 
                    name="Honoraires Année Précédente" 
                    stroke="#9CA3AF" 
                    strokeWidth={2}
                    strokeDasharray="5 5"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Performance par société */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Performance par Société</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.companyPerformance}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="company" />
                  <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`} />
                  <Tooltip formatter={(value: any) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="ca" name="Honoraires Réalisés" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Performers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Négociateurs */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
                <Users className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Top 10 Négociateurs</span>
              </div>
              <div className="divide-y divide-slate-100">
                {data.topNegotiators.map((negotiator, index) => (
                  <div key={index} className="flex items-center justify-between px-5 py-3 hover:bg-indigo-50/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-amber-100 text-amber-700' :
                        index === 1 ? 'bg-slate-100 text-slate-600' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-50 text-slate-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{negotiator.name}</p>
                        <p className="text-xs text-slate-400">{negotiator.transactions} vente{negotiator.transactions !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-slate-800">{formatCurrency(negotiator.ca)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Agences */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
                <Building2 className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-700">Top 10 Agences</span>
              </div>
              <div className="divide-y divide-slate-100">
                {data.topAgencies.map((agency, index) => (
                  <div key={index} className="flex items-center justify-between px-5 py-3 hover:bg-indigo-50/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-amber-100 text-amber-700' :
                        index === 1 ? 'bg-slate-100 text-slate-600' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-slate-50 text-slate-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">{agency.name}</p>
                        <p className="text-xs text-slate-400">{agency.transactions} vente{agency.transactions !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <p className="text-sm font-bold text-slate-800">{formatCurrency(agency.ca)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'commercial' && (
        <div className="space-y-6">
          {/* Analyse des mandats */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Performance par Type de Mandat</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.mandateAnalysis}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis yAxisId="left" tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`} />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="ca" name="Honoraires" fill="#3B82F6" />
                <Bar yAxisId="right" dataKey="count" name="Nombre" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Performance par origine */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Top 10 Origines de Leads</h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={data.originPerformance} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`} />
                <YAxis type="category" dataKey="origin" width={150} />
                <Tooltip formatter={(value: any) => formatCurrency(value)} />
                <Bar dataKey="ca" name="Honoraires" fill="#8B5CF6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Performance par type de bien */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Performance par Type de Bien</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">CA</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Prix Moyen</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Durée Moy.</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {data.propertyTypePerformance.map((type, index) => (
                    <tr key={index} className="border-b border-slate-100 last:border-0 hover:bg-indigo-50/30 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{type.type}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{type.count}</td>
                      <td className="px-6 py-4 text-sm text-right font-bold text-blue-600">{formatCurrency(type.ca)}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{formatCurrency(type.averagePrice)}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{Math.round(type.averageDuration)} jours</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'temporal' && (
        <div className="space-y-6">

          {/* ── Délai Mandat → Compromis par type de mandat ── */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
              <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center">
                <Clock size={14} className="text-amber-600" />
              </div>
              <span className="text-sm font-bold text-slate-700">Délai Mandat → Compromis par type de mandat</span>
              <span className="ml-auto text-xs text-slate-400">
                {mandatToCompromisDelays.reduce((s, d) => s + d.count, 0)} transaction{mandatToCompromisDelays.reduce((s, d) => s + d.count, 0) !== 1 ? 's' : ''} analysée{mandatToCompromisDelays.reduce((s, d) => s + d.count, 0) !== 1 ? 's' : ''}
              </span>
            </div>

            {mandatToCompromisDelays.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-slate-400">
                Aucune transaction avec date de mandat et date de compromis sur cette période.
              </div>
            ) : (
              <div className="p-5">
                <div className="space-y-4">
                  {mandatToCompromisDelays.map(d => {
                    const maxDays = Math.max(...mandatToCompromisDelays.map(x => x.avgDays));
                    const pct = maxDays > 0 ? (d.avgDays / maxDays) * 100 : 0;
                    const color = d.avgDays <= 30 ? 'bg-emerald-500' : d.avgDays <= 90 ? 'bg-amber-500' : 'bg-red-500';
                    const textColor = d.avgDays <= 30 ? 'text-emerald-600' : d.avgDays <= 90 ? 'text-amber-600' : 'text-red-600';
                    return (
                      <div key={d.type} className="flex items-center gap-4">
                        <div className="w-24 shrink-0">
                          <span className="text-sm font-semibold text-slate-700">{d.type}</span>
                          <p className="text-xs text-slate-400">{d.count} vente{d.count > 1 ? 's' : ''}</p>
                        </div>
                        <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${color}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <button
                          onClick={() => setMandatDelayModal({ type: d.rawType, label: d.type })}
                          className={`w-24 text-right text-sm font-bold ${textColor} hover:underline cursor-pointer`}
                        >
                          {d.avgDays} jours
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Légende */}
                <div className="flex items-center gap-4 mt-5 pt-4 border-t border-slate-100">
                  <span className="text-xs text-slate-400 font-medium">Légende :</span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" /> ≤ 30 j</span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" /> 31–90 j</span>
                  <span className="flex items-center gap-1.5 text-xs text-slate-500"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" /> &gt; 90 j</span>
                </div>
              </div>
            )}
          </div>

          {/* Cycles de vente */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <Clock size={18} className="text-indigo-600" />
                </div>
                <InfoTooltip title="Délai Compromis → Acte" description="Durée moyenne entre la signature du compromis et la signature de l'acte authentique." formula="Moyenne(Date Acte - Date Compromis)" />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">Compromis → Acte</p>
              <p className="text-2xl font-bold text-slate-800">{Math.round(data.salesCycles.averageCompromisToActe)} jours</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Clock size={18} className="text-emerald-600" />
                </div>
                <InfoTooltip title="Délai Acte → Encaissement" description="Durée moyenne entre la signature de l'acte et l'encaissement des honoraires." formula="Moyenne(Date Encaissement - Date Acte)" />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">Acte → Encaissement</p>
              <p className="text-2xl font-bold text-slate-800">{Math.round(data.salesCycles.averageActeToEncaissement)} jours</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                  <Clock size={18} className="text-violet-600" />
                </div>
                <InfoTooltip title="Cycle Total" description="Durée totale moyenne du processus de vente, du compromis à l'encaissement." formula="Compromis → Acte + Acte → Encaissement" />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">Cycle Total</p>
              <p className="text-2xl font-bold text-slate-800">{Math.round(data.salesCycles.averageTotalCycle)} jours</p>
            </div>
          </div>

          {/* Évolution mensuelle détaillée */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Évolution Mensuelle Détaillée</h3>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={data.monthlyEvolution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`} />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="ca" name="Honoraires Actuels" fill="#3B82F6" />
                <Line yAxisId="left" type="monotone" dataKey="previousYearCA" name="Honoraires N-1" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 5" />
                <Line yAxisId="right" type="monotone" dataKey="transactions" name="Nb Transactions" stroke="#10B981" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Tableau mensuel */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Détail Mensuel</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Mois</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Honoraires Actuels</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Honoraires N-1</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Évolution</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Transactions</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {data.monthlyEvolution.map((month, index) => {
                    const evolution = month.previousYearCA > 0 
                      ? ((month.ca - month.previousYearCA) / month.previousYearCA) * 100 
                      : 0;
                    
                    return (
                      <tr key={index} className="border-b border-slate-100 last:border-0 hover:bg-indigo-50/30 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{month.month}</td>
                        <td className="px-6 py-4 text-sm text-right font-bold text-blue-600">{formatCurrency(month.ca)}</td>
                        <td className="px-6 py-4 text-sm text-right text-gray-600">{formatCurrency(month.previousYearCA)}</td>
                        <td className={`px-6 py-4 text-sm text-right font-medium ${
                          evolution > 0 ? 'text-green-600' : evolution < 0 ? 'text-red-600' : 'text-gray-600'
                        }`}>
                          {evolution > 0 ? '+' : ''}{evolution.toFixed(1)}%
                        </td>
                        <td className="px-6 py-4 text-sm text-right text-gray-900">{month.transactions}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'financial' && (
        <div className="space-y-6">
          {/* Analyse des rétrocessions */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Analyse des Rétrocessions</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.retrocessionAnalysis}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" />
                <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`} />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
                          <p className="font-semibold text-gray-800 mb-1">{label}</p>
                          <p className="text-amber-600">Montant : {formatCurrency(d.total)}</p>
                          <p className="text-slate-600">Nombre : {d.count} rétrocession{d.count > 1 ? 's' : ''}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar dataKey="total" name="Montant Total" fill="#F59E0B" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Tableau des rétrocessions */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Détail des Rétrocessions</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Nombre</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Montant Total</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">% des Honoraires</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {data.retrocessionAnalysis.map((retro, index) => (
                    <tr key={index} className="border-b border-slate-100 last:border-0 hover:bg-indigo-50/30 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{retro.type}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{retro.count}</td>
                      <td className="px-6 py-4 text-sm text-right font-bold text-amber-600">{formatCurrency(retro.total)}</td>
                      <td className="px-6 py-4 text-sm text-right text-gray-900">{retro.percentage.toFixed(2)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Répartition des rétrocessions */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Répartition des Rétrocessions</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.retrocessionAnalysis}
                  dataKey="total"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                >
                  {data.retrocessionAnalysis.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {activeTab === 'forecast' && (
        <div className="space-y-6">
          {/* Pipeline prévisionnel */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                  <FileText size={18} className="text-amber-600" />
                </div>
                <InfoTooltip title="Honoraires Potentiels" description="Chiffre d'affaires total des transactions en statut potentiel." formula="Σ(Honoraires nets des transactions potentielles)" />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">Honoraires Potentiels</p>
              <p className="text-xl font-bold text-slate-800">{formatCurrency(data.pipeline.potentialCA)}</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <FileText size={18} className="text-indigo-600" />
                </div>
                <InfoTooltip title="Honoraires Compromis" description="Chiffre d'affaires total des transactions en statut compromis." formula="Σ(Honoraires nets des transactions compromis)" />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">Honoraires Compromis</p>
              <p className="text-xl font-bold text-slate-800">{formatCurrency(data.pipeline.compromisCA)}</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Target size={18} className="text-emerald-600" />
                </div>
                <InfoTooltip title="Honoraires Prévisionnels" description="Projection des honoraires finaux basée sur les taux de conversion historiques." formula="Honoraires Réalisés + (Compromis × 80%) + (Potentiel × 30%)" />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">Honoraires Prévisionnels</p>
              <p className="text-xl font-bold text-slate-800">{formatCurrency(data.pipeline.expectedCA)}</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center">
                  <TrendingUp size={18} className="text-violet-600" />
                </div>
                <InfoTooltip title="Probabilité" description="Taux de conversion global historique utilisé pour les prévisions." formula="Taux de conversion global" />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">Probabilité</p>
              <p className="text-xl font-bold text-slate-800">{data.pipeline.probability.toFixed(1)}%</p>
            </div>
          </div>

          {/* Graphique prévisionnel */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Projection de Fin de Période</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: 'Honoraires Réalisés', value: data.totalCA },
                { name: 'Honoraires Compromis (80%)', value: data.pipeline.compromisCA * 0.8 },
                { name: 'Honoraires Potentiels (30%)', value: data.pipeline.potentialCA * 0.3 },
                { name: 'Objectif', value: data.totalObjective }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k€`} />
                <Tooltip formatter={(value: any) => formatCurrency(value)} />
                <Bar dataKey="value" fill="#8B5CF6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Analyse de gap */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Analyse de Gap</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-xl border border-slate-200">
                <span className="text-sm font-medium text-slate-600">Honoraires Réalisés</span>
                <span className="text-sm font-bold text-slate-800">{formatCurrency(data.totalCA)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-indigo-50 rounded-xl border border-indigo-100">
                <span className="text-sm font-medium text-indigo-700">Honoraires Prévisionnels</span>
                <span className="text-sm font-bold text-indigo-800">{formatCurrency(data.pipeline.expectedCA)}</span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-100">
                <span className="text-sm font-medium text-emerald-700">Objectif</span>
                <span className="text-sm font-bold text-emerald-800">{formatCurrency(data.totalObjective)}</span>
              </div>
              <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
                data.pipeline.expectedCA >= data.totalObjective ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
              }`}>
                <span className={`text-sm font-medium ${data.pipeline.expectedCA >= data.totalObjective ? 'text-emerald-700' : 'text-red-700'}`}>
                  Gap à combler
                </span>
                <span className={`text-sm font-bold ${data.pipeline.expectedCA >= data.totalObjective ? 'text-emerald-800' : 'text-red-800'}`}>
                  {formatCurrency(Math.max(0, data.totalObjective - data.pipeline.expectedCA))}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Délai Mandat → Compromis ── */}
      {mandatDelayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setMandatDelayModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                <Clock size={15} className="text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">Mandat {mandatDelayModal.label} — Délai jusqu'au compromis</p>
                <p className="text-xs text-slate-400">{mandatDelayModalRows.length} mandat{mandatDelayModalRows.length > 1 ? 's' : ''} — du plus long au plus court</p>
              </div>
              <button onClick={() => setMandatDelayModal(null)} className="ml-auto p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                <X size={16} />
              </button>
            </div>

            {/* Liste */}
            <div className="overflow-y-auto flex-1">
              {mandatDelayModalRows.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-10">Aucune donnée disponible.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500">#</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500">N° mandat</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500">Date mandat</th>
                      <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500">Date compromis</th>
                      <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500">Délai</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {mandatDelayModalRows.map((row, i) => {
                      const textColor = row.days <= 30 ? 'text-emerald-600' : row.days <= 90 ? 'text-amber-600' : 'text-red-600';
                      const fmt = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                      return (
                        <tr key={row.id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                          <td className="px-5 py-3 text-slate-400 font-medium">{i + 1}</td>
                          <td className="px-5 py-3 text-slate-700 font-medium">{row.propertyNumber}</td>
                          <td className="px-5 py-3 text-slate-500">{fmt(row.mandateDate)}</td>
                          <td className="px-5 py-3 text-slate-500">{fmt(row.compromiseDate)}</td>
                          <td className={`px-5 py-3 text-right font-bold ${textColor}`}>{row.days} j</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}